import { QueryResult } from "@/utils/mergeQueryStates"
import { ReactElement, ReactNode } from "react"

interface QueryStateHandlerProps<T> {
   query: {
      data?: T | null | undefined
   } & QueryResult
   data?: T | null | undefined // Custom data
   children: (data: T) => ReactNode | ReactElement | Element | Element[]
   emptyMessage?: string
   showEmptyMessage?: boolean
   treatFetchingAsLoading?: boolean
   minHeight?: string
   emptyArrayIsValidData?: boolean
   isLoading?: boolean
}

export function QueryRender<T>({
   query,
   data: customData,
   children,
   emptyMessage = "Rien à afficher !",
   showEmptyMessage = false,
   treatFetchingAsLoading = false,
   minHeight,
   emptyArrayIsValidData = false,
   isLoading,
}: QueryStateHandlerProps<T>) {
   const data = customData || query.data
   const { isError, error } = query
   const minHeightClass = minHeight ? `min-h-[${minHeight}]` : "min-h-[400px]"

   if (query.isLoading || (treatFetchingAsLoading && query.isFetching) || isLoading) {
      return (
         <div className={`flex items-center justify-center ${minHeightClass}`}>
            <span className="loading loading-spinner loading-lg"></span>
         </div>
      )
   }

   if (isError) {
      return (
         <div className={`flex items-center justify-center ${minHeightClass}`}>
            <div className="alert alert-error m-20 p-10">
               <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="stroke-current shrink-0 h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
               >
                  <path
                     strokeLinecap="round"
                     strokeLinejoin="round"
                     strokeWidth="2"
                     d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
               </svg>
               <span>Une erreur est survenue : {error?.message}</span>
            </div>
         </div>
      )
   }

   if (
      !data ||
      (!emptyArrayIsValidData && data instanceof Array && data.length === 0) ||
      showEmptyMessage
   ) {
      return (
         <div className={`flex items-center justify-center ${minHeightClass}`}>
            <div className="alert m-10">
               <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="size-6"
               >
                  <path
                     strokeLinecap="round"
                     strokeLinejoin="round"
                     d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z"
                  />
               </svg>
               <span>{emptyMessage}</span>
            </div>
         </div>
      )
   }

   return <>{children(data)}</>
}
